/** Plain time value (already a number or null) → display string. Clamped at
 * 0 - a race time is never negative, but a live-clock caller computing
 * elapsed-since-start can land a hair below zero for an instant (the
 * shared tick only updates every 100ms, so it can briefly read just before
 * a freshly-arrived start timestamp) - (-0.001).toFixed(2) prints "-0.00",
 * not "0.00", so this needs an explicit floor rather than relying on
 * toFixed's own (correct, but insufficient) handling of exact -0. */
export function fmtTime(t) {
  return t == null ? '—' : Math.max(0, t).toFixed(2)
}

/**
 * A rider's entry in results[].competitors[].sessions[code]. The backend
 * (publishers.py::_build_results) sets time:0 for a flagged run and omits
 * the key entirely for a session the rider hasn't run yet - those need
 * different rendering, not both collapsing to the same blank/dash.
 */
export function fmtSessionTime(session) {
  if (!session) return { text: '·', flagged: false, value: null }
  if (!session.time) return { text: 'FLAG', flagged: true, value: null }
  return { text: session.time.toFixed(2), flagged: false, value: session.time }
}

function fallbackGateLabel(field, gate) {
  if (field === 'launch_time') return 'Launch'
  const m = field ? /^(split|speed)(\d+)$/.exec(field) : null
  if (m) return `${m[1] === 'split' ? 'Split' : 'Speed'} ${m[2]}`
  return field || `Split ${gate}`
}

/**
 * Stable per-gate identity for joining a `venue.splits` column against a
 * row's own `session.gates` entry. timing-bridge's Python publisher gives
 * every gate a `field` (`launch_time`/`split1`/...) - the real identity,
 * see gateColumns' own doc comment on why `gate` number alone isn't safe
 * for that provider. NIGAN's own VB.NET publisher (hardware-timed events -
 * confirmed live at Wiscombe 2026-09-13, `nigan/timing/venue` publishing
 * `{"gate":1,"label":"Bridge"}` with no `field`/`type` at all) never sends
 * `field` - NIGAN's own `web/live.html` doesn't need it, matching gate
 * position against its own per-venue `config.js` instead. Falling back to
 * a `field`-only join there silently matched every column to whichever
 * gate the lookup found first (all having `field === undefined`) - every
 * split column showed gate 1's value. `field` when present, else a
 * synthetic key from `gate` number - safe for NIGAN since a hardware
 * event's gate position is fixed (unlike a poll-based feed's, which can
 * have an early split genuinely missing on one run).
 */
export function gateKey(g) {
  return g.field || `gate:${g.gate}`
}

/** Real per-gate `type` ('T'/'S'), for a `venue.splits` column whose own
 * `type` is missing (NIGAN hardware events don't send it - see gateKey
 * above) - found by checking what any row that's actually recorded this
 * gate reported. Defaults to 'T' (the common case) if truly nothing has
 * landed yet, so a column doesn't misrender before its first value. */
function discoverGateType(rows, session, gateNum) {
  for (const r of rows) {
    const g = r.sessions?.[session]?.gates?.find((g) => g.gate === gateNum)
    if (g?.type) return g.type
  }
  return 'T'
}

/**
 * One column per distinct gate field seen across `rows` for `session`,
 * ordered by the backend's `gate` number - each field's fixed position in
 * that hill's real report-column order (timing-bridge's
 * HILL_SPLIT_LABELS/_gate_field_order), NOT a hardcoded "all splits then
 * all speeds" assumption. Real venues interleave the two - e.g. Gurston's
 * "Hollow" speed trap sits between the launch gate and the first split,
 * not after every split. `gate` is deliberately an *absolute* position
 * (stable regardless of which fields any specific run has), so it's safe
 * to order by even when rows have different fields present - a provider's
 * beam not triggering for one run doesn't shift everyone else's columns
 * (see the 2026-08-02 Gurston backfill, where exactly this happened
 * before _build_gates and this grouping were both fixed).
 *
 * `label` is the real venue-specific name (e.g. Gurston's "S1"/"Int."/
 * "K-A") when the backend attached one, else a generic "Split N"/
 * "Speed N" derived from the field name itself. `launch_time` (the
 * reaction/launch gate) only ever becomes a column when it has a real
 * label - without one there's no way to know if it's meaningful to show.
 *
 * `knownSplits` (the `venue` topic's `splits` field, when present) is the
 * hill's full known column set regardless of what's actually landed in the
 * data yet - used as-is when available so every real column for a known
 * hill shows from the very first row, instead of only what's been
 * discovered so far (a class/session where every rider's beam happened not
 * to report a given split - real, not rare, at Gurston - would otherwise
 * look like that column doesn't exist at all). Falls back to discovering
 * columns from `rows` themselves for a hill with no known mapping.
 */
export function gateColumns(rows, session, knownSplits) {
  if (knownSplits && knownSplits.length) {
    return [...knownSplits]
      .filter((g) => g.field !== 'launch_time' || g.label)
      .sort((a, b) => a.gate - b.gate)
      .map((g) => ({
        field: gateKey(g),
        gate: g.gate,
        type: g.type || discoverGateType(rows, session, g.gate),
        label: g.label || fallbackGateLabel(g.field, g.gate),
      }))
  }
  const byKey = new Map()
  for (const r of rows) {
    const gates = r.sessions?.[session]?.gates || []
    for (const g of gates) {
      const key = gateKey(g)
      if (!byKey.has(key)) byKey.set(key, g)
    }
  }
  return [...byKey.values()]
    .filter((g) => g.field !== 'launch_time' || g.label)
    .sort((a, b) => a.gate - b.gate)
    .map((g) => ({ field: gateKey(g), gate: g.gate, type: g.type, label: g.label || fallbackGateLabel(g.field, g.gate) }))
}

/** One row's value (or null) for a given gate column, within one session.
 * `field` here is a gateColumns() column's `field` (already a gateKey -
 * see its comment), matched against each row gate via the same key so a
 * NIGAN gate with no `field` of its own still joins correctly. */
export function gateValueFor(session, field) {
  const g = session?.gates?.find((g) => gateKey(g) === field)
  return g ? g.value : null
}

/**
 * timing-bridge/publishers.py's _rider_name() joins sidecar/multi-seat
 * entries as "Driver & Passenger" in the single `rider` field it publishes -
 * split that back apart here so the display can give the passenger their
 * own line instead of running the whole thing together as one long string.
 * No " & " in the source string means a solo rider - passenger comes back
 * null rather than an empty string, so callers can just check truthiness.
 */
export function splitRiderName(rider) {
  const i = rider ? rider.indexOf(' & ') : -1
  return i === -1 ? [rider, null] : [rider.slice(0, i), rider.slice(i + 3)]
}

export function riderMatchesQuery(competitor, query) {
  if (!query) return true
  const q = query.toLowerCase()
  return (
    competitor.rider?.toLowerCase().includes(q) ||
    competitor.machine?.toLowerCase().includes(q) ||
    String(competitor.num).includes(q)
  )
}
